import { normalizeRole, ROLE_INFO, type Permission } from '@console/shared'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowUpRight,
  BookOpen,
  Building2,
  Check,
  ChevronUp,
  CreditCard,
  FlaskConical,
  Home,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Monitor,
  Moon,
  Plus,
  Sun,
  UserRound,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { api, requestOwnOrganization, useActiveOrg, useMe } from '../../lib/api'
import { authClient } from '../../lib/auth'
import { useTheme } from '../../theme/ThemeContext'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { ErrorText } from '../ui/misc'
import { useToast } from '../ui/toast'
import { useDocsUrl } from '../DocsLink'
import { Wordmark } from './Logo'

type NavItem = { to: string; label: string; icon: LucideIcon; needs?: Permission }

const NAV: NavItem[] = [
  { to: '/home', label: 'Home', icon: Home },
  { to: '/playground', label: 'Playground', icon: FlaskConical, needs: 'playground.run' },
  { to: '/usage', label: 'Usage', icon: LayoutDashboard, needs: 'usage.view' },
  { to: '/api-keys', label: 'API Keys', icon: KeyRound, needs: 'keys.view' },
  { to: '/docs', label: 'Documentation', icon: BookOpen },
]

/** The organization's own pages (each a page of its own), listed straight after the main items. */
const ORG_NAV: NavItem[] = [
  { to: '/settings/members', label: 'Members', icon: Users, needs: 'usage.view' },
  { to: '/settings/billing', label: 'Billing', icon: CreditCard, needs: 'billing.view' },
]

const ITEM = 'sidebar-nav-item group mx-2 flex items-center gap-2.5 rounded-lg px-2.5 py-[7px]'

function NavItemLink({ item: { to, label, icon: Icon }, onClick }: { item: NavItem; onClick: () => void }) {
  const docsUrl = useDocsUrl()
  // the docs live on the docs site when the deployment has one: open it in a new tab
  if (to === '/docs' && docsUrl)
    return (
      <a href={docsUrl} target="_blank" rel="noopener noreferrer" onClick={onClick} className={`${ITEM} text-ink-secondary hover:bg-surface-hover hover:text-ink`}>
        <Icon size={17} strokeWidth={1.6} className="shrink-0 opacity-80" />
        <span className="flex-1 truncate text-left text-[13.5px] leading-5">{label}</span>
        <ArrowUpRight size={14} strokeWidth={1.6} className="shrink-0 text-ink-muted opacity-0 transition-opacity group-hover:opacity-100" />
      </a>
    )
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `${ITEM} ${
          isActive ? 'bg-surface-raised text-ink shadow-token-sm ring-1 ring-[color:var(--border-subtle)]' : 'text-ink-secondary hover:bg-surface-hover hover:text-ink'
        }`
      }
    >
      <Icon size={17} strokeWidth={1.6} className="shrink-0 opacity-80" />
      <span className="flex-1 truncate text-left text-[13.5px] leading-5">{label}</span>
    </NavLink>
  )
}

function MenuItem({ icon: Icon, label, onClick }: { icon: LucideIcon; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] font-[450] text-ink hover:bg-surface-hover">
      <Icon className="h-3.5 w-3.5 text-ink-muted" strokeWidth={1.8} />
      {label}
    </button>
  )
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')
}

function NewOrgModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<unknown>(null)
  const qc = useQueryClient()
  const create = async () => {
    setBusy(true)
    setErr(null)
    try {
      const { id } = await api<{ id: string }>('/api/orgs', { body: { name } })
      await authClient.organization.setActive({ organizationId: id })
      await qc.invalidateQueries()
      onClose()
      setName('')
    } catch (e) {
      setErr(e)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New organization"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={create} loading={busy} disabled={name.trim().length < 2}>
            Create
          </Button>
        </>
      }
    >
      <input className="input" autoFocus placeholder="Acme Inc." value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim().length >= 2 && create()} />
      <div className="mt-3">
        <ErrorText error={err} />
      </div>
    </Modal>
  )
}

export function Sidebar({ mobileOpen, onCloseMobile }: { mobileOpen: boolean; onCloseMobile: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [newOrg, setNewOrg] = useState(false)
  const { theme, setTheme } = useTheme()
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const { data: me } = useMe()
  const { org, can } = useActiveOrg()

  useEffect(() => {
    const onDoc = (e: MouseEvent) => !menuRef.current?.contains(e.target as Node) && setMenuOpen(false)
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const switchOrg = async (id: string) => {
    await authClient.organization.setActive({ organizationId: id })
    qc.clear()
    setMenuOpen(false)
    navigate('/home')
  }

  const invites = me?.pendingInvitations ?? []
  const waiting = me?.access === 'pending' || me?.access === 'rejected'
  const [busyInvite, setBusyInvite] = useState<string | null>(null)
  const respond = async (id: string, accept: boolean) => {
    setBusyInvite(id)
    const { error } = accept
      ? await authClient.organization.acceptInvitation({ invitationId: id })
      : await authClient.organization.rejectInvitation({ invitationId: id })
    setBusyInvite(null)
    if (error) return
    if (accept) await authClient.organization.setActive({ organizationId: invites.find((i) => i.id === id)!.orgId })
    qc.clear()
    setMenuOpen(false)
    navigate('/home')
  }

  const logout = async () => {
    await authClient.signOut()
    qc.clear()
    navigate('/login')
  }

  const go = (to: string) => {
    setMenuOpen(false)
    onCloseMobile()
    navigate(to)
  }

  const themeModes = [
    { mode: 'light' as const, Icon: Sun, label: 'Light' },
    { mode: 'dark' as const, Icon: Moon, label: 'Dark' },
    { mode: 'system' as const, Icon: Monitor, label: 'System' },
  ]
  const name = me?.user?.name || me?.user?.email || ''

  const content = (
    <div className="flex h-full flex-col bg-surface-sunken">
      <div className="flex items-center justify-between px-4 pb-4 pt-5">
        <Link to="/home" onClick={onCloseMobile}>
          <Wordmark />
        </Link>
        <button type="button" className="rounded-md p-1.5 text-ink-muted hover:bg-surface-hover md:hidden" onClick={onCloseMobile} aria-label="Close menu">
          <X className="h-4 w-4" />
        </button>
      </div>

      <nav className="sidebar-nav-primary mt-2 flex flex-1 flex-col space-y-px">
        {[...NAV, ...ORG_NAV]
          .filter((n) => !n.needs || can(n.needs))
          .map((n) => (
            <NavItemLink key={n.to} item={n} onClick={onCloseMobile} />
          ))}
      </nav>

      <div className="mt-auto space-y-2 border-t border-line-subtle p-2">
        <div ref={menuRef} className="relative">
          {menuOpen && (
            <div className="absolute bottom-[calc(100%+6px)] left-0 z-50 w-[272px] max-w-[calc(100vw-24px)] animate-fade-in overflow-hidden rounded-xl border border-line bg-surface-raised shadow-token-lg">
              <div className="flex items-center justify-between gap-2 border-b border-line-subtle px-3 py-2.5">
                <div className="min-w-0 truncate text-[12px] text-ink-muted">{me?.user?.email}</div>
                <button type="button" onClick={logout} className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-brand hover:opacity-80">
                  <LogOut className="h-3 w-3" /> Log out
                </button>
              </div>

              {org ? (
                <div className="border-b border-line-subtle px-1.5 py-1.5">
                  <div className="px-2 pb-1 pt-1">
                    <div className="eyebrow !text-[10px]">Organization</div>
                    <div className="mt-0.5 flex items-center justify-between gap-2">
                      <span className="truncate text-[13.5px] font-semibold text-ink">{org.name}</span>
                      <span className="shrink-0 text-[11px] text-ink-muted">{ROLE_INFO[org.role].label}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-b border-line-subtle px-1.5 py-1.5">
                  <MenuItem icon={Building2} label={waiting ? 'Your access request' : 'Join or create an organization'} onClick={() => go('/home')} />
                </div>
              )}

              <div className="border-b border-line-subtle px-1.5 py-1.5">
                <div className="flex items-center justify-between px-2 pb-1 pt-1">
                  <span className="eyebrow !text-[10px]">Switch organization</span>
                  {!waiting && me?.canCreateOrg && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[11.5px] font-medium text-ink-secondary hover:bg-surface-hover hover:text-ink"
                      onClick={() => {
                        setMenuOpen(false)
                        setNewOrg(true)
                      }}
                    >
                      <Plus className="h-3 w-3" /> New
                    </button>
                  )}
                  {!waiting && me && !me.canCreateOrg && (
                    <button
                      type="button"
                      title="Ask for an organization of your own"
                      className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[11.5px] font-medium text-ink-secondary hover:bg-surface-hover hover:text-ink"
                      onClick={async () => {
                        setMenuOpen(false)
                        try {
                          await requestOwnOrganization()
                          toast("Requested. We'll email you when your organization is ready.")
                        } catch (e) {
                          toast((e as Error).message, 'error')
                        }
                      }}
                    >
                      <Plus className="h-3 w-3" /> Request your own
                    </button>
                  )}
                </div>
                <div className="max-h-44 overflow-y-auto">
                  {(me?.orgs ?? []).map((o) => {
                    const active = o.id === me?.activeOrgId
                    return (
                      <button
                        key={o.id}
                        type="button"
                        disabled={active}
                        onClick={() => switchOrg(o.id)}
                        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] ${active ? 'cursor-default' : 'hover:bg-surface-hover'}`}
                      >
                        <span className={`min-w-0 flex-1 truncate ${active ? 'font-medium text-ink' : 'text-ink-secondary'}`}>{o.name}</span>
                        <span className="shrink-0 text-[11px] text-ink-muted">{ROLE_INFO[o.role].label}</span>
                        <Check className={`h-3.5 w-3.5 shrink-0 text-brand ${active ? '' : 'invisible'}`} />
                      </button>
                    )
                  })}
                  {!me?.orgs?.length && <div className="px-2 py-1.5 text-[12px] text-ink-muted">You aren't in an organization yet.</div>}
                </div>
                {invites.length > 0 && (
                  <div className="mt-1 space-y-1 border-t border-line-subtle px-2 pt-2">
                    <div className="eyebrow !text-[10px]">Invitations</div>
                    {invites.map((i) => (
                      <div key={i.id} className="flex items-center gap-2 py-0.5">
                        <Mail className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
                        <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink" title={`as ${ROLE_INFO[normalizeRole(i.role)].label}`}>
                          {i.orgName}
                        </span>
                        <button
                          type="button"
                          disabled={busyInvite != null}
                          onClick={() => respond(i.id, true)}
                          className="shrink-0 rounded-md bg-brand px-2.5 py-0.5 text-[11.5px] font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-50"
                        >
                          {busyInvite === i.id ? '…' : 'Join'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-2.5 py-2">
                <div className="flex overflow-hidden rounded-md border border-line">
                  {themeModes.map(({ mode, Icon, label }) => (
                    <button
                      key={mode}
                      type="button"
                      title={label}
                      aria-label={label}
                      onClick={() => setTheme(mode)}
                      className={`flex flex-1 items-center justify-center py-2 transition-colors ${
                        theme === mode ? 'bg-surface-hover text-ink' : 'text-ink-muted hover:bg-surface-hover/70 hover:text-ink'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-t border-line-subtle px-1.5 py-1.5">
                {org && <MenuItem icon={Building2} label="Organization settings" onClick={() => go('/settings/organization')} />}
                <MenuItem icon={UserRound} label="Personal settings" onClick={() => go('/settings/profile')} />
              </div>
            </div>
          )}
          <button type="button" onClick={() => setMenuOpen((v) => !v)} className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-surface-hover">
            {me?.user?.image ? (
              <img src={me.user.image} alt="" className="h-8 w-8 shrink-0 rounded-full" referrerPolicy="no-referrer" />
            ) : (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold" style={{ background: 'rgb(var(--trodo-primary-rgb) / 0.14)', color: 'var(--trodo-primary)' }}>
                {initials(name)}
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-[450] leading-[19.5px] text-ink">{name}</span>
              <span className="block truncate text-[11px] text-ink-muted">
                {org ? `${org.name} · ${ROLE_INFO[org.role].label}` : waiting ? 'Waiting for access' : 'No organization'}
              </span>
            </span>
            <ChevronUp className={`h-3.5 w-3.5 text-ink-muted transition-transform ${menuOpen ? '' : 'rotate-180'}`} />
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <aside className="hidden h-full w-52 shrink-0 md:block">{content}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-[80] md:hidden">
          <button type="button" className="absolute inset-0" style={{ background: 'var(--surface-overlay)' }} onClick={onCloseMobile} aria-label="Close sidebar" />
          <aside className="absolute left-0 top-0 h-full w-[85vw] max-w-[320px] shadow-token-lg">{content}</aside>
        </div>
      )}
      <NewOrgModal open={newOrg} onClose={() => setNewOrg(false)} />
    </>
  )
}

export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn-ghost !p-1.5 md:hidden" onClick={onClick} aria-label="Open menu">
      <Menu className="h-5 w-5" />
    </button>
  )
}
